import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { IDetailFetcher } from '~src/httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '~src/httpClient/fetchers/IListFetcher';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import {
  MarkRecentlyViewedRequest,
  RecentlyViewedObjectType
} from '~src/recents/requests/MarkRecentlyViewedRequest';
import {
  ISimulationDetailInfo,
  ISimulationInfo
} from '~src/simulation/models/ISimulationInfo';
import { SimulationMapper } from '~src/simulation/models/mappers/SimulationMapper';
import { AddTagsToSimulationsRequest } from '~src/simulation/serverApi/requests/AddTagsToSimulationsRequest';
import { DeleteSimulationsRequest } from '~src/simulation/serverApi/requests/DeleteSimulationsRequest';
import { GetSimulationActionsRequest } from '~src/simulation/serverApi/requests/GetSimulationActionsRequest';
import { GetSimulationsRequest } from '~src/simulation/serverApi/requests/GetSimulationsRequest';
import { MoveSimulationsToProject } from '~src/simulation/serverApi/requests/MoveSimulationsToProjectRequest';
import { RemoveTagsFromSimulationsRequest, } from '~src/simulation/serverApi/requests/RemoveTagsFromSimulationsRequest';
import {
  ISimulationDescriptionParams,
  SetSimulationDescriptionRequest,
} from '~src/simulation/serverApi/requests/SetSimulationDescriptionRequest';
import { SetSimulationNameRequest } from '~src/simulation/serverApi/requests/SetSimulationNameRequest';
import { SetSimulationsOwnerRequest } from '~src/simulation/serverApi/requests/SetSimulationsOwnerRequest';
import { ISimulationActions } from '~src/simulation/serverApi/types/ISimulationActions';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';

import WebHttpClient from '../../httpClient/WebHttpClient';

class SimulationsResource implements IListResource<ISimulationInfo>, IFieldValuesResource, IDetailResource<ISimulationDetailInfo>, IActionResource<ISimulationActions> {

  private listFetcher: IListFetcher<ISimulationInfo> = new WebClientListResourceFetcher(GetSimulationsRequest, ServerApiMapper.simulations);
  private detailFetcher: IDetailFetcher<ISimulationDetailInfo> = new WebClientDetailFetcher('getSimulation', SimulationMapper.mapDetail);
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public isSimulationInfo(item: IContentInfo): item is ISimulationInfo {
    return item &&
      item.contentType &&
      item.contentType === 'simulation';
  }

  public all(): ResourceQuery<ISimulationInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<ISimulationDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'simulation');
  }

  // Actions
  public actions(id: string): Promise<ISimulationActions> {
    return WebHttpClient.sendRequest(new GetSimulationActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetSimulationNameRequest({
      id: id,
      name: name
    }));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteSimulationsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetSimulationsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, simulationIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(simulationIds,
        (idsBlock) => new MoveSimulationsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToSimulationsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromSimulationsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: ISimulationDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetSimulationDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  };

  public markRecentlyViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Simulation
    }));
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage?: string): any {
    if (result.errors && result.errors.length > 0) {
      if (failureMessage) {
        ToasterService.getInstance().pop({
          type: ToastType.Error,
          text: failureMessage
        });
      } else {
        result.errors.forEach((error: IError) => {
          ToasterService.getInstance().pop({
            type: ToastType.Error,
            text: error.message
          });
        });
      }
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }

  public getSimulationIconPath(contentInfo: ISpaceItemDetailInfo<'simulation'>, headerIcon: boolean = true): string {
    if (headerIcon) {
      return imgPath(`simulation_60px_card.svg`);
    } else {
      return imgPath(`simulation_16px_medium.svg`);
    }
  }

}

export const Simulations = new SimulationsResource();
